import { useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Phone, Mail } from "lucide-react";

const apiBase = import.meta.env.VITE_API_BASE;

export default function ExamsProSupport() {
    const [sent, setSent] = useState(false);
    const [loading, setLoading] = useState(false);

    async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();
        const form = e.currentTarget;
        const data = {
            name: (form.elements.namedItem("name") as HTMLInputElement).value,
            email: (form.elements.namedItem("email") as HTMLInputElement).value,
            company: (form.elements.namedItem("company") as HTMLInputElement).value,
            message: (form.elements.namedItem("message") as HTMLTextAreaElement).value,
            product: "ExamsPro",
        };

        setLoading(true);
        try {
            const res = await fetch(`${apiBase}/api/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(data),
            });

            if (res.ok) {
                setSent(true);
            } else {
                console.error("API error:", await res.text());
                alert("Something went wrong, please try again later.");
            }
        } catch (err) {
            console.error(err);
            alert("Failed to connect to the server.");
        } finally {
            setLoading(false);
        }
    }

    return (
        <main className="min-h-screen bg-gradient-to-b from-indigo-700 via-indigo-600 to-violet-600 py-16 px-6 flex items-center justify-center">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-xl border border-indigo-100 p-8">
                {/* 🧭 Header */}
                <div className="text-center mb-8">
                    <img
                        src="/Images/ExamsPro.png"
                        alt="ExamsPro Logo"
                        className="w-14 h-14 mx-auto mb-3"
                    />
                    <h1 className="text-3xl font-bold text-indigo-700">ExamsPro Support</h1>
                    <p className="mt-1 text-gray-600 text-sm">
                        We're here to help with technical, account, or testing issues.
                    </p>
                </div>

                {/* ⚡ Quick Contact Options */}
                <div className="grid sm:grid-cols-3 gap-4 mb-8">
                    <a
                        href="https://secureidtechnologies.com/chat"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex flex-col items-center justify-center border border-indigo-100 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 p-4 transition transform hover:scale-[1.02] hover:shadow-md"
                    >
                        <MessageCircle className="w-6 h-6 mb-1" />
                        <span className="font-medium">Chat</span>
                    </a>

                    <a
                        href="tel:+17867763664"
                        className="flex flex-col items-center justify-center border border-indigo-100 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 p-4 transition transform hover:scale-[1.02] hover:shadow-md"
                    >
                        <Phone className="w-6 h-6 mb-1" />
                        <span className="font-medium">Call</span>
                    </a>

                    <a
                        href="mailto:support@secureidtechnologies.com"
                        className="flex flex-col items-center justify-center border border-indigo-100 rounded-xl bg-indigo-50 hover:bg-indigo-100 text-indigo-700 p-4 transition transform hover:scale-[1.02] hover:shadow-md"
                    >
                        <Mail className="w-6 h-6 mb-1" />
                        <span className="font-medium">Email</span>
                    </a>
                </div>

                <hr className="my-6 border-gray-200" />

                {/* 🧾 Contact Form */}
                {!sent ? (
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Name</label>
                            <input
                                name="name"
                                required
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                name="email"
                                required
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Organization / Agency</label>
                            <input
                                name="company"
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Message</label>
                            <textarea
                                name="message"
                                rows={5}
                                required
                                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full rounded-xl bg-indigo-600 px-5 py-3 text-white font-semibold hover:bg-indigo-700 transition transform hover:scale-[1.01]"
                        >
                            {loading ? "Sending..." : "Send Message"}
                        </button>
                    </form>
                ) : (
                    <div className="mt-6 rounded-xl border border-green-200 bg-green-50 p-4 text-green-800 text-center font-medium">
                        ✅ Thank you! Our ExamsPro Support team will reach out shortly.
                    </div>
                )}

                {/* ⬅ Back Link */}
                <p className="mt-8 text-center text-sm text-gray-600">
                    <Link
                        to="/solutions/examspro"
                        className="inline-flex items-center text-indigo-600 hover:text-indigo-700 hover:underline"
                    >
                        ← Back to ExamsPro
                    </Link>
                </p>
            </div>
        </main>
    );
}
